import useSafeState from "../utils/safe-state";
import {Button, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin} from "antd";
import React, {useEffect} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import moment from "moment";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const MscStockDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);

    const [supplierOptions, setSupplierOptions] = useSafeState<any[]>([]);
    const [supplierLoading, setSupplierLoading] = useSafeState(false);
    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);
    const [ddsRuleOptions, setDdsRuleOptions] = useSafeState<any[]>([]);
    const [ddsRuleLoading, setDdsRuleLoading] = useSafeState(false);
    const [userSupplierCode, setUserSupplierCode] = useSafeState<string | null>(null);

    useEffect(() => {
        const supplierCode = localStorage.getItem('supplierCode');
        setUserSupplierCode(supplierCode === 'undefined' ? null : supplierCode);
        form.resetFields();
        
        if (supplierCode === 'undefined' || supplierCode === null) {
            getSuppliers();
        } else {
            // If user has supplier code, set it and load items
            form.setFieldsValue({ supplierCode: supplierCode });
            getItems(supplierCode);
        }

        if(props.mode === 'Edit' && data && data.id) {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if(key === 'ddsNumberValidFrom' || key === 'ddsNumberValidTo') {
                    fieldsValues.push({name: key, value: value ? moment(value as string) : null});
                } else if(key === 'item' && value && typeof value === 'object') {
                    const item = value as any;
                    fieldsValues.push({name: ['item', 'id'], value: item.id});
                    // Set supplier code from item
                    if (item.supplier && item.supplier.code) {
                        fieldsValues.push({name: 'supplierCode', value: item.supplier.code});
                        getItems(item.supplier.code);
                    }
                    // Load DDS rules when item is set
                    if (item.id) {
                        getDdsRules(item.id);
                    }
                } else if(key === 'ddsAssignmentRule' && value && typeof value === 'object') {
                    fieldsValues.push({name: ['ddsAssignmentRule', 'id'], value: (value as any).id});
                } else {
                    fieldsValues.push({name: key, value: value});
                }
            }
            form.setFields(fieldsValues);
        }
    }, [props.mode]);

    const getSuppliers = () => {
        if (axiosInstance.current != null) {
            setSupplierLoading(true);
            axiosInstance.current
                .get('/resource/suppliers')
                .then(response => {
                    const suppliers = Array.isArray(response.data) ? response.data : [];
                    setSupplierOptions(suppliers);
                })
                .catch(function (error: any) {
                    console.error('Error loading suppliers:', error);
                    openNotification('error', 'Failed to load suppliers');
                })
                .finally(() => {
                    setSupplierLoading(false);
                });
        }
    };

    const getItems = (supplierCode: string) => {
        if (axiosInstance.current != null && supplierCode) {
            setItemLoading(true);
            axiosInstance.current
                .post('/resource/items/page', {
                    start: 0,
                    length: 10000,
                    direction: 'asc',
                    sorted_field: 'id',
                    query: {
                        supplierCode: supplierCode
                    }
                })
                .then(response => {
                    const items = (response.data && response.data.data) || [];
                    setItemOptions(items);
                })
                .catch(function (error: any) {
                    console.error('Error loading items:', error);
                    openNotification('error', 'Failed to load items');
                })
                .finally(() => {
                    setItemLoading(false);
                });
        } else {
            setItemOptions([]);
        }
    };

    const getDdsRules = (itemId: number) => {
        if (axiosInstance.current != null && itemId) {
            setDdsRuleLoading(true);
            axiosInstance.current
                .post('/resource/dds-assignment-rules/page', {
                    start: 0,
                    length: 1000,
                    direction: 'asc',
                    sorted_field: 'id',
                    query: {
                        item: {
                            id: itemId
                        }
                    }
                })
                .then(response => {
                    const rules = (response.data && response.data.data) || [];
                    setDdsRuleOptions(rules);
                })
                .catch(function (error: any) {
                    console.error('Error loading DDS rules:', error);
                    openNotification('error', 'Failed to load DDS assignment rules');
                })
                .finally(() => {
                    setDdsRuleLoading(false);
                });
        } else {
            setDdsRuleOptions([]);
        }
    };

    const handleSupplierChange = (supplierCode: string) => {
        form.setFieldsValue({
            item: undefined,
            ddsAssignmentRule: undefined,
            ddsNumber: undefined,
            ddsNumberValidFrom: undefined,
            ddsNumberValidTo: undefined
        });
        setItemOptions([]);
        setDdsRuleOptions([]);
        if (supplierCode) {
            getItems(supplierCode);
        }
    };

    const handleItemChange = (itemId: number) => {
        form.setFieldsValue({
            ddsAssignmentRule: undefined,
            ddsNumber: undefined,
            ddsNumberValidFrom: undefined,
            ddsNumberValidTo: undefined
        });
        getDdsRules(itemId);
    };

    const handleDdsRuleChange = (ddsRuleId: number) => {
        const selectedRule = ddsRuleOptions.find(rule => rule.id === ddsRuleId);
        if (selectedRule) {
            form.setFieldsValue({
                ddsNumber: selectedRule.ddsNumber,
                ddsNumberValidFrom: selectedRule.validFrom ? moment(selectedRule.validFrom) : null,
                ddsNumberValidTo: selectedRule.validTo ? moment(selectedRule.validTo) : null
            });
        } else {
            form.setFieldsValue({
                ddsNumber: undefined,
                ddsNumberValidFrom: undefined,
                ddsNumberValidTo: undefined
            });
        }
    };

    const handleEdit = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .put(
                    '/resource/msc-stock/' + encodeURIComponent(data.id),
                    {
                        id: data.id,
                        item: {
                            id: (values.item && values.item.id) || values.item
                        },
                        ddsAssignmentRule: {
                            id: (values.ddsAssignmentRule && values.ddsAssignmentRule.id) || values.ddsAssignmentRule
                        },
                        quantity: values.quantity,
                        ddsNumber: values.ddsNumber,
                        ddsNumberValidFrom: values.ddsNumberValidFrom ? moment(values.ddsNumberValidFrom).format('YYYY-MM-DD HH:mm') : null,
                        ddsNumberValidTo: values.ddsNumberValidTo ? moment(values.ddsNumberValidTo).format('YYYY-MM-DD HH:mm') : null,
                        version: form.getFieldValue(['version'])
                    },
                    config,
                )
                .then(() => {
                    openNotification('success', 'Msc Stock successfully updated.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const onReset = () => {
        form.resetFields();
        setItemOptions([]);
        setDdsRuleOptions([]);
        const supplierCode = localStorage.getItem('supplierCode');
        if (supplierCode && supplierCode !== 'undefined') {
            form.setFieldsValue({ supplierCode: supplierCode });
            getItems(supplierCode);
        }
    };

    const handleFinish = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .post(
                    '/resource/msc-stock',
                    {
                        item: {
                            id: (values.item && values.item.id) || values.item
                        },
                        ddsAssignmentRule: {
                            id: (values.ddsAssignmentRule && values.ddsAssignmentRule.id) || values.ddsAssignmentRule
                        },
                        quantity: values.quantity,
                        ddsNumber: values.ddsNumber,
                        ddsNumberValidFrom: values.ddsNumberValidFrom ? moment(values.ddsNumberValidFrom).format('YYYY-MM-DD HH:mm') : null,
                        ddsNumberValidTo: values.ddsNumberValidTo ? moment(values.ddsNumberValidTo).format('YYYY-MM-DD HH:mm') : null
                    },
                    config,
                )
                .then(() => {
                    openNotification('success', 'Msc Stock successfully created.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    return (
        <Form
            id="newMscStock"
            layout='vertical'
            form={form}
            onFinish={(values) => (props.mode === 'Edit' ? handleEdit(values) : handleFinish(values))}
        >
            <Spin spinning={loading || supplierLoading || itemLoading || ddsRuleLoading}>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="supplierCode"
                            label={<Trans>Supplier</Trans>}
                            hidden={userSupplierCode != null}
                            rules={[{ required: userSupplierCode == null, message: <Trans>Please select a supplier</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select supplier</Trans>}
                                optionFilterProp="children"
                                disabled={props.mode === 'Edit' || userSupplierCode != null}
                                onChange={handleSupplierChange}
                                filterOption={(input, option) => {
                                    const children = option && option.children;
                                    const text = (children as unknown as string) || '';
                                    return text.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {supplierOptions.map(supplier => (
                                    <Select.Option key={supplier.code} value={supplier.code}>
                                        {supplier.code} - {supplier.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name={['item', 'id']}
                            label={<Trans>Item</Trans>}
                            rules={[{ required: true, message: <Trans>Please select an item</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                                disabled={props.mode === 'Edit' || (!form.getFieldValue('supplierCode') && userSupplierCode == null)}
                                onChange={handleItemChange}
                                filterOption={(input, option) => {
                                    const children = option && option.children;
                                    const text = (children as unknown as string) || '';
                                    return text.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name={['ddsAssignmentRule', 'id']}
                            label={<Trans>DDS Assignment Rule</Trans>}
                            rules={[{ required: true, message: <Trans>Please select a DDS assignment rule</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select DDS assignment rule</Trans>}
                                optionFilterProp="children"
                                onChange={handleDdsRuleChange}
                                disabled={!form.getFieldValue(['item', 'id'])}
                                filterOption={(input, option) => {
                                    const children = option && option.children;
                                    const text = (children as unknown as string) || '';
                                    return text.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {ddsRuleOptions.map(rule => (
                                    <Select.Option key={rule.id} value={rule.id}>
                                        {rule.ddsNumber} {rule.item && rule.item.itemNo ? `(${rule.item.itemNo})` : ''}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="quantity"
                            label={<Trans>Quantity</Trans>}
                            rules={[{ required: true, message: <Trans>Please enter quantity</Trans> }]}
                        >
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="ddsNumber"
                            label={<Trans>DDS Number</Trans>}
                        >
                            <Input disabled style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            name="ddsNumberValidFrom"
                            label={<Trans>DDS Valid From</Trans>}
                            rules={[{ required: true, message: <Trans>Please select valid from date</Trans> }]}
                        >
                            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ddsNumberValidTo"
                            label={<Trans>DDS Valid To</Trans>}
                            rules={[{ required: true, message: <Trans>Please select valid to date</Trans> }]}
                        >
                            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div>
                {props.mode === 'Edit'
                    ? (<Button htmlType="submit" type="primary">
                        <Trans>Save</Trans>
                    </Button>)
                    :
                    <>
                        <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                            <Trans>Clear</Trans>
                        </Button>
                        <Button
                            loading={loading}
                            htmlType="submit"
                            type="primary"
                            style={{ marginRight: 8 }}
                        >
                            <Trans>Save</Trans>
                        </Button>
                    </>
                }
            </div>
        </Form>
    );

}
export default MscStockDrawer;
